/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';

import { PaginatedOauthClientsResponseSchema } from '@/client';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { OauthClientFilters } from '@/hooks/query/usePaginatedOAuthClientsQuery';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

// Type for individual OAuth client from paginated response
type OAuthClient = PaginatedOauthClientsResponseSchema['data'][number];

export type OAuthClientsTableProps = {
  oauthClientsData: PaginatedOauthClientsResponseSchema;
  oauthClientFilters: OauthClientFilters;
  updateQueryParams: UpdateParams<OAuthClient, Partial<OauthClientFilters>>;
  deleteOAuthClient: (oauthClientId: string, oauthClientName: string) => void;
  primaryAction?: React.ReactNode;
};

export function OAuthClientsTable({
  oauthClientsData,
  updateQueryParams,
  oauthClientFilters,
  deleteOAuthClient,
  primaryAction,
}: OAuthClientsTableProps) {
  const { t } = useMimicTranslation('oauthClients');
  const navigate = useNavigate();
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const handleViewClient = (client: OAuthClient) => {
    navigate(`/tenants/${selectedTenantId}/oauth-clients/${client.id}`);
  };

  const columns = getOAuthClientColumns(t, oauthClientFilters, handleViewClient, deleteOAuthClient);

  return (
    <PaginatedTable
      data={oauthClientsData}
      columns={columns}
      rowKey={(config) => config.id}
      data-testid="oauth-clients-table"
      onParamsChange={updateQueryParams}
      primaryAction={primaryAction}
    />
  );
}

function getOAuthClientColumns(
  t: MimicTranslationFunction<'oauthClients'>,
  oauthClientFilters: OauthClientFilters,
  viewClient: (client: OAuthClient) => void,
  deleteOAuthClient: (oauthClientId: string, oauthClientName: string) => void,
): ColumnsType<OAuthClient> {
  return [
    {
      title: <span data-testid="oauthClients.clientId">{t('table.clientId')}</span>,
      dataIndex: 'clientId',
      render: (clientId: string) => {
        return (
          <Text mono copyable size="sm">
            {clientId}
          </Text>
        );
      },
      width: '200px',
    },
    {
      title: <span data-testid="oauthClients.name">{t('table.name')}</span>,
      dataIndex: 'name',
      sorter: true,
      filterMode: 'menu',
      render: (name) => {
        return (
          <Flex gap="xxs">
            <MimicHighlighter searchText={oauthClientFilters?.name} text={name} />
          </Flex>
        );
      },
    },
    {
      title: <span data-testid="oauthClients.redirectUris">{t('table.redirectUris')}</span>,
      dataIndex: 'redirectUris',
      render: (redirectUris: string[]) => {
        if (!redirectUris || redirectUris.length === 0) {
          return <Text type="subtle">-</Text>;
        }

        const firstUri = redirectUris[0];
        const truncatedFirstUri = firstUri.length > 30 ? `${firstUri.substring(0, 30)}...` : firstUri;

        const displayText = redirectUris.length === 1 ? truncatedFirstUri : truncatedFirstUri;
        const moreCount = redirectUris.length > 1 ? ` +${redirectUris.length - 1} ${t('table.more')}` : '';

        const tooltipContent = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {redirectUris.map((uri) => (
              <code key={uri} style={{ fontSize: '12px' }}>
                {uri}
              </code>
            ))}
          </div>
        );

        return (
          <Tooltip title={tooltipContent}>
            <span>
              <code
                style={{
                  background: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}
              >
                {displayText}
              </code>
              {moreCount && (
                <Text type="subtle" style={{ marginLeft: '4px', fontSize: '12px' }}>
                  {moreCount}
                </Text>
              )}
            </span>
          </Tooltip>
        );
      },
      width: '200px',
    },
    {
      title: <span data-testid="oauthClients.actions">{t('table.actions')}</span>,
      dataIndex: 'actions',
      render: (_, oauthClient: OAuthClient) => (
        <Flex gap="xs">
          <Button dataTestId="view-oauth-client" type="default" onClick={() => viewClient(oauthClient)}>
            {t('table.view')}
          </Button>
          <Button
            dataTestId="delete-oauth-client"
            type="primary"
            onClick={() => deleteOAuthClient(oauthClient.id, oauthClient.name)}
          >
            {t('table.delete')}
          </Button>
        </Flex>
      ),
      width: '200px',
    },
  ];
}
